import React, { useState } from "react";
import { useHistory } from "react-router-dom";
import { useIntl } from "react-intl";
import { Button, Input, InputNumber, Modal, notification, Select, Table, Typography } from "antd";
import { MinusCircleOutlined, SearchOutlined, SwapOutlined } from "@ant-design/icons";
import { carpetByRef, categories, clientName, dimensionsText, findClient } from "../../mocks/carpets";
import CleanStatus from "./CleanStatus";
import { useCarpetPreview } from "./context";

const { Title } = Typography;

const emptyDraft = () => ({
  clientId: null,
  category: null,
  length: null,
  width: null,
  description: "",
  storageBox: "",
  inNotes: "",
  price: null,
});

const emptyClient = () => ({
  firstName: "",
  lastName: "",
  phone: "",
  street: "",
  city: "",
});

const lineKey = () => `${Date.now()}-${Math.random()}`;

export default function CheckIn() {
  const intl = useIntl();
  const history = useHistory();
  const { state, openStayFor, createClient, createCarpet, checkIn } = useCarpetPreview();
  const [ref, setRef] = useState("");
  const [searchError, setSearchError] = useState(null);
  const [lines, setLines] = useState([]);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [draft, setDraft] = useState(emptyDraft);
  const [draftError, setDraftError] = useState(null);
  const [clientOpen, setClientOpen] = useState(false);
  const [clientDraft, setClientDraft] = useState(emptyClient);
  const [clientError, setClientError] = useState(null);

  const search = (value) => {
    if (!String(value || "").trim()) return;
    const carpet = carpetByRef(state, value);
    if (!carpet) {
      setSearchError("refNotFound");
      return;
    }
    if (openStayFor(carpet.id) || lines.some((line) => line.carpetId === carpet.id)) {
      setSearchError(openStayFor(carpet.id) ? "alreadyIn" : "duplicateRef");
      return;
    }
    setLines((current) => current.concat({ key: lineKey(), carpetId: carpet.id, storageBox: "", inNotes: "", cleaned: false }));
    setRef("");
    setSearchError(null);
  };

  const openDialog = () => {
    setDraft(emptyDraft());
    setDraftError(null);
    setDialogOpen(true);
  };

  const calculatePrice = () => {
    const category = draft.category;
    const dimensions = { length: draft.length, width: draft.width };
    if (!category) return;
    const categoryCalculator = (state.priceCalculator || []).find((item) => item.clientItemCategory === category);
    if (!categoryCalculator) {
      setDraftError("priceCalculatorNotFoundForThisCategory");
      notification.warning({
        message: intl.formatMessage({ id: "notFound" }),
        description: intl.formatMessage({ id: "priceCalculatorNotFoundForThisCategory" }),
      });
      return;
    }
    setDraftError(null);
    const { algorithm } = categoryCalculator;
    const price = eval(algorithm);
    if (isNaN(price)) return;
    setDraft((current) => ({ ...current, price }));
  };

  const openClientDialog = () => {
    setClientDraft(emptyClient());
    setClientError(null);
    setClientOpen(true);
  };

  const saveClient = () => {
    if (!clientDraft.firstName.trim() && !clientDraft.lastName.trim()) {
      setClientError("fieldRequired");
      return;
    }
    const client = createClient(clientDraft);
    setDraft((current) => ({ ...current, clientId: client.id }));
    setClientOpen(false);
  };

  const saveCarpet = () => {
    if (!draft.clientId || !draft.category) {
      setDraftError("fieldRequired");
      return;
    }
    const carpet = createCarpet(draft);
    setLines((current) => current.concat({
      key: lineKey(),
      carpetId: carpet.id,
      storageBox: draft.storageBox,
      inNotes: draft.inNotes,
      cleaned: false,
    }));
    setSearchError(null);
    setDialogOpen(false);
  };

  const submit = () => {
    if (!lines.length) return;
    const blocked = lines.find((line) => openStayFor(line.carpetId));
    if (blocked) {
      setSearchError("alreadyIn");
      return;
    }
    const bundle = checkIn(lines.map((line) => ({
      carpetId: line.carpetId,
      storageBox: line.storageBox,
      inNotes: line.inNotes,
      cleaned: line.cleaned,
    })));
    history.push({ pathname: "/preview/report", state: { stayIds: bundle.stays.map((stay) => stay.id), bundle } });
  };

  return (
    <div>
      <Title level={4} style={{ marginTop: 0 }}>{intl.formatMessage({ id: "checkIn" })}</Title>
      <div className="no-print" style={{ display: "flex", gap: 8, alignItems: "center", marginBottom: 12 }}>
        <Input.Search
          value={ref}
          placeholder={intl.formatMessage({ id: "ref" })}
          style={{ width: 180 }}
          enterButton={<SearchOutlined />}
          onChange={(event) => { setRef(event.target.value); setSearchError(null); }}
          onSearch={search}
        />
        {searchError && <span style={{ color: "#cf1322" }}>{intl.formatMessage({ id: searchError })}</span>}
        <Button onClick={openClientDialog}>{intl.formatMessage({ id: "newClient" })}</Button>
        <Button onClick={openDialog}>{intl.formatMessage({ id: "newCarpet" })}</Button>
        <Button type="primary" style={{ marginLeft: "auto" }} disabled={!lines.length} onClick={submit}>
          {intl.formatMessage({ id: "checkIn" })}
        </Button>
      </div>
      <Table
        size="small"
        bordered
        pagination={false}
        rowKey="key"
        dataSource={lines}
        columns={[
          { title: intl.formatMessage({ id: "ref" }), width: 100, render: (line) => (state.carpets.find((item) => item.id === line.carpetId) || {}).ref },
          {
            title: intl.formatMessage({ id: "client" }),
            width: 150,
            ellipsis: true,
            render: (line) => {
              const carpet = state.carpets.find((item) => item.id === line.carpetId);
              return carpet ? clientName(findClient(state, carpet.clientId)) : "";
            },
          },
          {
            title: intl.formatMessage({ id: "category" }),
            width: 110,
            render: (line) => {
              const carpet = state.carpets.find((item) => item.id === line.carpetId);
              return carpet ? intl.formatMessage({ id: carpet.category }) : "";
            },
          },
          {
            title: intl.formatMessage({ id: "price" }),
            width: 80,
            render: (line) => {
              const carpet = state.carpets.find((item) => item.id === line.carpetId);
              return carpet && carpet.price != null ? carpet.price : "";
            },
          },
          {
            title: intl.formatMessage({ id: "widthXLength" }),
            width: 140,
            render: (line) => dimensionsText(state.carpets.find((item) => item.id === line.carpetId)),
          },
          {
            title: intl.formatMessage({ id: "storageBox" }),
            width: 120,
            render: (line) => (
              <Input
                size="small"
                value={line.storageBox}
                onChange={(event) => {
                  const value = event.target.value;
                  setLines((current) => current.map((item) => (item.key === line.key ? { ...item, storageBox: value } : item)));
                }}
              />
            ),
          },
          {
            title: intl.formatMessage({ id: "cleanStatus" }),
            width: 160,
            render: (line) => {
              const action = intl.formatMessage({ id: "cleanStatus" });
              return (
                <span style={{ display: "flex", gap: 4, alignItems: "center" }}>
                  <CleanStatus cleaned={line.cleaned} />
                  <Button
                    size="small"
                    type="primary"
                    icon={<SwapOutlined />}
                    aria-label={action}
                    title={action}
                    onClick={() => setLines((current) => current.map((item) => (item.key === line.key ? { ...item, cleaned: !item.cleaned } : item)))}
                  />
                </span>
              );
            },
          },
          {
            title: intl.formatMessage({ id: "inNotes" }),
            render: (line) => (
              <Input
                size="small"
                value={line.inNotes}
                onChange={(event) => {
                  const value = event.target.value;
                  setLines((current) => current.map((item) => (item.key === line.key ? { ...item, inNotes: value } : item)));
                }}
              />
            ),
          },
          {
            title: "",
            width: 48,
            render: (line) => (
              <Button
                type="text"
                icon={<MinusCircleOutlined />}
                aria-label={intl.formatMessage({ id: "remove" })}
                title={intl.formatMessage({ id: "remove" })}
                onClick={() => setLines((current) => current.filter((item) => item.key !== line.key))}
              />
            ),
          },
        ]}
      />
      <Modal
        title={intl.formatMessage({ id: "newCarpet" })}
        visible={dialogOpen}
        width={520}
        onCancel={() => setDialogOpen(false)}
        onOk={saveCarpet}
        destroyOnClose
      >
        <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr 1fr", gap: 8 }}>
          <Select
            placeholder={intl.formatMessage({ id: "client" })}
            style={{ width: "100%", gridColumn: "1 / span 2" }}
            value={draft.clientId}
            onChange={(clientId) => { setDraft((current) => ({ ...current, clientId })); setDraftError(null); }}
          >
            {state.clients.map((item) => (
              <Select.Option key={item.id} value={item.id}>#{item.ref} {clientName(item)}</Select.Option>
            ))}
          </Select>
          <Select
            placeholder={intl.formatMessage({ id: "category" })}
            style={{ width: "100%" }}
            value={draft.category}
            onChange={(category) => { setDraft((current) => ({ ...current, category })); setDraftError(null); }}
          >
            {categories.map((category) => (
              <Select.Option key={category} value={category}>{intl.formatMessage({ id: category })}</Select.Option>
            ))}
          </Select>
          <InputNumber placeholder={intl.formatMessage({ id: "width" })} style={{ width: "100%" }} value={draft.width} min={0} onChange={(width) => setDraft((current) => ({ ...current, width }))} />
          <InputNumber placeholder={intl.formatMessage({ id: "length" })} style={{ width: "100%" }} value={draft.length} min={0} onChange={(length) => setDraft((current) => ({ ...current, length }))} />
          <Input
            placeholder={intl.formatMessage({ id: "storageBox" })}
            value={draft.storageBox}
            onChange={(event) => { const storageBox = event.target.value; setDraft((current) => ({ ...current, storageBox })); }}
          />
          <Input
            placeholder={intl.formatMessage({ id: "description" })}
            style={{ gridColumn: "1 / -1" }}
            value={draft.description}
            onChange={(event) => { const description = event.target.value; setDraft((current) => ({ ...current, description })); }}
          />
          <Input.TextArea
            placeholder={intl.formatMessage({ id: "inNotes" })}
            style={{ gridColumn: "1 / -1" }}
            autoSize={{ minRows: 2, maxRows: 4 }}
            value={draft.inNotes}
            onChange={(event) => { const inNotes = event.target.value; setDraft((current) => ({ ...current, inNotes })); }}
          />
          <div style={{ gridColumn: "1 / -1", display: "flex", gap: 8 }}>
            <InputNumber
              placeholder={intl.formatMessage({ id: "price" })}
              style={{ width: 140 }}
              min={0}
              value={draft.price}
              onChange={(price) => setDraft((current) => ({ ...current, price }))}
            />
            <Button onClick={calculatePrice}>{intl.formatMessage({ id: "priceCalculator" })}</Button>
          </div>
          {draftError && <span style={{ gridColumn: "1 / -1", color: "#cf1322" }}>{intl.formatMessage({ id: draftError })}</span>}
        </div>
      </Modal>
      <Modal
        title={intl.formatMessage({ id: "newClient" })}
        visible={clientOpen}
        width={420}
        onCancel={() => setClientOpen(false)}
        onOk={saveClient}
        destroyOnClose
      >
        <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 8 }}>
          <Input
            placeholder={intl.formatMessage({ id: "firstName" })}
            value={clientDraft.firstName}
            onChange={(event) => {
              const firstName = event.target.value;
              setClientDraft((current) => ({ ...current, firstName }));
              setClientError(null);
            }}
          />
          <Input
            placeholder={intl.formatMessage({ id: "lastName" })}
            value={clientDraft.lastName}
            onChange={(event) => {
              const lastName = event.target.value;
              setClientDraft((current) => ({ ...current, lastName }));
              setClientError(null);
            }}
          />
          <Input
            placeholder={intl.formatMessage({ id: "phone" })}
            style={{ gridColumn: "1 / -1" }}
            value={clientDraft.phone}
            onChange={(event) => {
              const phone = event.target.value;
              setClientDraft((current) => ({ ...current, phone }));
            }}
          />
          <Input
            placeholder={intl.formatMessage({ id: "street" })}
            style={{ gridColumn: "1 / -1" }}
            value={clientDraft.street}
            onChange={(event) => {
              const street = event.target.value;
              setClientDraft((current) => ({ ...current, street }));
            }}
          />
          <Input
            placeholder={intl.formatMessage({ id: "city" })}
            style={{ gridColumn: "1 / -1" }}
            value={clientDraft.city}
            onChange={(event) => {
              const city = event.target.value;
              setClientDraft((current) => ({ ...current, city }));
            }}
          />
          {clientError && <span style={{ gridColumn: "1 / -1", color: "#cf1322" }}>{intl.formatMessage({ id: clientError })}</span>}
        </div>
      </Modal>
    </div>
  );
}
